import { Button, Modal, Table } from 'antd';
import * as React from 'react';
import { CheckIcon, DocumentsIcon, ExclamationCircleIcon } from '../../custom_shared/icons';
import { DuplicateClaimRow, DuplicateClaimsLayoutConfiguration } from '../../common/services/types';

type Props = {
    duplicateClaims?: DuplicateClaimsLayoutConfiguration | null;
};

const getValue = (row: DuplicateClaimRow, camelKey: keyof DuplicateClaimRow, pascalKey: keyof DuplicateClaimRow) =>
    row[camelKey] ?? row[pascalKey] ?? '';

const formatDate = (value: unknown) => {
    if (!value) {
        return '';
    }

    const text = String(value);
    return text.length > 10 ? text.substring(0, 10) : text;
};

const DuplicateClaimsNotification: React.FC<Props> = ({ duplicateClaims }) => {
    const [isDismissed, setIsDismissed] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const claims = duplicateClaims?.claims ?? duplicateClaims?.Claims ?? [];
    const rawStatus = duplicateClaims?.status ?? duplicateClaims?.Status;
    const hasDuplicateClaims = claims.length > 0;
    const status = rawStatus === 'found' && !hasDuplicateClaims ? 'none' : rawStatus;
    const message =
        status === 'none' && rawStatus === 'found'
            ? 'No duplicate claims found.'
            : (duplicateClaims?.message ?? duplicateClaims?.Message);
    const checkedBusinessId = duplicateClaims?.checkedBusinessId ?? duplicateClaims?.CheckedBusinessId;
    const checkedDateOfLoss = duplicateClaims?.checkedDateOfLoss ?? duplicateClaims?.CheckedDateOfLoss;
    const resultKey = `${status || ''}|${message || ''}|${checkedBusinessId || ''}|${checkedDateOfLoss || ''}|${claims.length}`;

    React.useEffect(() => {
        setIsDismissed(false);
        setIsModalOpen(false);
    }, [resultKey]);

    if (!duplicateClaims || !status || isDismissed) {
        return null;
    }

    const close = () => {
        setIsDismissed(true);
        setIsModalOpen(false);
    };

    const renderIcon = () => {
        if (status === 'found') {
            return <DocumentsIcon />;
        }

        if (status === 'none') {
            return <CheckIcon />;
        }

        return <ExclamationCircleIcon width={24} height={24} />;
    };

    return (
        <div className={`duplicate-claims-notification ${status}`}>
            {renderIcon()}
            <span className="info-label">{message}</span>
            <div className="duplicate-claims-notification-actions">
                {status === 'found' && (
                    <Button type="primary" onClick={() => setIsModalOpen(true)}>
                        View
                    </Button>
                )}
                <Button onClick={close}>Close</Button>
            </div>
            <Modal
                className="duplicate-claims-modal"
                title="Potential duplicate claims"
                visible={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        Close
                    </Button>
                ]}
                width={1100}
            >
                <Table
                    rowKey={record =>
                        `${getValue(record, 'claimId', 'ClaimId') || 'claim'}-${getValue(record, 'businessId', 'BusinessId')}`
                    }
                    dataSource={claims}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                    columns={[
                        {
                            title: 'Claim ID',
                            render: (_: unknown, row: DuplicateClaimRow) => getValue(row, 'claimId', 'ClaimId')
                        },
                        {
                            title: 'Business ID',
                            render: (_: unknown, row: DuplicateClaimRow) => getValue(row, 'businessId', 'BusinessId')
                        },
                        {
                            title: 'UW Year',
                            render: (_: unknown, row: DuplicateClaimRow) => getValue(row, 'uwYear', 'UwYear')
                        },
                        {
                            title: 'Insured Period Start',
                            render: (_: unknown, row: DuplicateClaimRow) =>
                                formatDate(getValue(row, 'insuredPeriodStart', 'InsuredPeriodStart'))
                        },
                        {
                            title: 'Type of Business',
                            render: (_: unknown, row: DuplicateClaimRow) =>
                                getValue(row, 'typeOfBusName', 'TypeOfBusName') ||
                                getValue(row, 'typeOfBusCode', 'TypeOfBusCode')
                        },
                        {
                            title: 'Loss Start Date',
                            render: (_: unknown, row: DuplicateClaimRow) =>
                                formatDate(getValue(row, 'lossStartDate', 'LossStartDate'))
                        },
                        {
                            title: 'Loss End Date',
                            render: (_: unknown, row: DuplicateClaimRow) =>
                                formatDate(getValue(row, 'lossEndDate', 'LossEndDate'))
                        },
                        {
                            title: 'Status',
                            render: (_: unknown, row: DuplicateClaimRow) =>
                                getValue(row, 'statusName', 'StatusName') || getValue(row, 'statusCode', 'StatusCode')
                        },
                        {
                            title: 'Cedent Name',
                            render: (_: unknown, row: DuplicateClaimRow) => getValue(row, 'cedentName', 'CedentName')
                        },
                        {
                            title: 'Insured Name',
                            render: (_: unknown, row: DuplicateClaimRow) => getValue(row, 'insuredName', 'InsuredName')
                        },
                        {
                            title: 'Loss Name',
                            render: (_: unknown, row: DuplicateClaimRow) => getValue(row, 'lossName', 'LossName')
                        }
                    ]}
                />
            </Modal>
        </div>
    );
};

export default DuplicateClaimsNotification;
